import { observer } from "mobx-react-lite";
import { useFormikContext } from "formik";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getLabelList, getOptionLabel } from "@/lib/utils";
import {
  LOCATION_OPTIONS,
  INDUSTRIAL_CLASSIFICATION,
  KEY_IMPROVEMENT,
} from "@/lib/constants";
import { useCompanyStore } from "../hooks";
import { useAuthStore } from "@/features/auth";

const CompanySetupSummary = observer(({ showTargetFrequency = false }) => {
  const { t } = useTran();
  const { values } = useFormikContext();
  const { setStep } = useCompanyStore();
  const { user } = useAuthStore();

  const targetFrequencyLabel =
    values.targetFrequency === "monthly" ? t("Monthly") : t("Yearly");
  const getEnabledLabel = (value) =>
    value === false ? t("Disabled") : t("Enabled");

  const rows = [
    { label: t("Company Name"), value: values.companyName || "—", step: 1 },
    {
      label: t("Country"),
      value: getOptionLabel(values.country, LOCATION_OPTIONS, "—"),
      step: 1,
    },
    ...(showTargetFrequency
      ? [
          ...(user?.role === "system_admin"
            ? [
                {
                  label: t("Dashboard"),
                  value:
                    values.dashboardVariant === "benchmark"
                      ? t("Benchmark")
                      : t("Standard"),
                  step: 1,
                },
              ]
            : []),
          {
            label: t("Target Setup Frequency"),
            value: targetFrequencyLabel,
            step: 1,
          },
          {
            label: t("Allow Monthly/Yearly Switching"),
            value: getEnabledLabel(values.enableMonthlyYearlySwitch),
            step: 1,
          },
          {
            label: t("Auto Switch Target Data Overview"),
            value: getEnabledLabel(values.enableViewDetailsSwitch),
            step: 1,
          },
          {
            label: t("Use JJ Organization Selector"),
            value: getEnabledLabel(values.useJJOrganizationSelector),
            step: 1,
          },
          {
            label: t("Show Only Metrics With Target And Data"),
            value: getEnabledLabel(values.showOnlyMetricsWithTargetAndData),
            step: 1,
          },
        ]
      : []),
    {
      label: t("Industry"),
      value: getOptionLabel(values.industry, INDUSTRIAL_CLASSIFICATION, "—"),
      step: 2,
    },
    { label: t("Employees"), value: values.employeesRange || "—", step: 3 },
    { label: t("Revenue (USD)"), value: values.revenueRange || "—", step: 3 },
    {
      label: t("Service Countries"),
      value: getLabelList(values.serviceCountries, LOCATION_OPTIONS, "—"),
      step: 4,
    },
    {
      label: t("Objectives"),
      value: getLabelList(values.objectives, KEY_IMPROVEMENT, "—"),
      step: 4,
    },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">
        {t("Review your details")}
      </h3>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {rows.map((row) => (
          <Card
            key={row.label}
            className="border border-border bg-background p-3"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  {row.label}
                </p>
                <p className="mt-1 break-words text-sm font-semibold text-foreground">
                  {row.value}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => setStep(row.step)}
              >
                {t("Edit")}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
});

export default CompanySetupSummary;
